import type { CSSProperties } from 'react';
import type { ColorScheme as ColorSchemeType } from '@/sanity.types';
import { stegaClean } from 'next-sanity';

export type HeaderTabColorOverride = {
  background: string;
  foreground: string;
};

/** Header tab pairings that differ from the standard ColorScheme map. */
const HEADER_TAB_OVERRIDES: Record<string, HeaderTabColorOverride> = {
  'how it works': {
    background: 'var(--color-pea)',
    foreground: 'var(--color-sky)',
  },
  download: {
    background: 'var(--color-sun)',
    foreground: 'var(--color-greyscale-charcoal)',
  },
  blog: {
    background: 'var(--color-blog-tab-background)',
    foreground: 'var(--color-blog-tab-foreground)',
  },
};

/** Sanity colorScheme values that need header-specific foreground pairings. */
const HEADER_SCHEME_OVERRIDES: Partial<
  Record<ColorSchemeType | 'charcoal', HeaderTabColorOverride>
> = {
  pea: {
    background: 'var(--color-pea)',
    foreground: 'var(--color-sky)',
  },
  'pea-light': {
    background: 'var(--color-pea-light)',
    foreground: 'var(--color-sky)',
  },
  sun: {
    background: 'var(--color-sun)',
    foreground: 'var(--color-greyscale-charcoal)',
  },
};

const COLOR_SCHEME_PAIRS: Record<
  ColorSchemeType | 'charcoal' | 'royal' | 'default',
  HeaderTabColorOverride
> = {
  pea: {
    background: 'var(--color-pea)',
    foreground: 'var(--color-sky)',
  },
  sky: {
    background: 'var(--color-sky)',
    foreground: 'var(--color-pea)',
  },
  blueberry: {
    background: 'var(--color-blueberry)',
    foreground: 'var(--color-grape)',
  },
  grape: {
    background: 'var(--color-grape)',
    foreground: 'var(--color-blueberry)',
  },
  plum: {
    background: 'var(--color-plum)',
    foreground: 'var(--color-tangerine)',
  },
  tangerine: {
    background: 'var(--color-tangerine)',
    foreground: 'var(--color-plum)',
  },
  royal: {
    background: 'var(--color-royal)',
    foreground: 'var(--color-lilac)',
  },
  lilac: {
    background: 'var(--color-lilac)',
    foreground: 'var(--color-royal)',
  },
  sun: {
    background: 'var(--color-sun)',
    foreground: 'var(--color-sky)',
  },
  'pea-light': {
    background: 'var(--color-pea-light)',
    foreground: 'var(--color-soft-black)',
  },
  'royal-light': {
    background: 'var(--color-royal-light)',
    foreground: 'var(--color-soft-black)',
  },
  charcoal: {
    background: 'var(--color-greyscale-charcoal)',
    foreground: 'var(--color-greyscale-vanilla)',
  },
  default: {
    background: 'var(--color-royal)',
    foreground: 'var(--color-lilac)',
  },
};

function normalizeTabLabel(text?: string | null): string {
  if (!text) return '';
  return stegaClean(text).trim().toLowerCase();
}

function normalizeColorScheme(
  scheme?: string | null
): ColorSchemeType | 'charcoal' | 'royal' {
  if (!scheme || scheme === 'default') return 'royal';
  return stegaClean(scheme) as ColorSchemeType | 'charcoal';
}

export function getHeaderTabColorOverride(
  linkText?: string | null,
  colorScheme?: string | null
): HeaderTabColorOverride | undefined {
  const normalizedText = normalizeTabLabel(linkText);
  if (normalizedText && HEADER_TAB_OVERRIDES[normalizedText]) {
    return HEADER_TAB_OVERRIDES[normalizedText];
  }

  const cleanScheme = normalizeColorScheme(colorScheme);
  return HEADER_SCHEME_OVERRIDES[cleanScheme];
}

export function resolveHeaderTabColors(
  linkText?: string | null,
  colorScheme?: string | null
): HeaderTabColorOverride {
  return (
    getHeaderTabColorOverride(linkText, colorScheme) ??
    COLOR_SCHEME_PAIRS[normalizeColorScheme(colorScheme)]
  );
}

export function headerTabStyle(
  linkText?: string | null,
  colorScheme?: string | null
): CSSProperties {
  const { background, foreground } = resolveHeaderTabColors(
    linkText,
    colorScheme
  );

  return {
    '--color-background': background,
    '--color-foreground': foreground,
  } as CSSProperties;
}

export function resolveHeaderTabColorScheme(
  scheme: string | null | undefined
): ColorSchemeType | 'charcoal' {
  return normalizeColorScheme(scheme) as ColorSchemeType | 'charcoal';
}
